import { Link } from '@tanstack/react-router'
import { MAX_RENDERED_CARDS, type RenderDataPayload } from './render-data-payload'
import { InquiryEntityCard } from './inquiry-entity-card'
import { EventEntityCard } from './event-entity-card'
import { ContactEntityCard } from './contact-entity-card'
import { TaskEntityCard } from './task-entity-card'

const LIST_ROUTE_BY_ENTITY_TYPE: Record<RenderDataPayload['entityType'], string> = {
  inquiry: '/leads',
  event: '/events',
  contact: '/contacts',
  task: '/tasks',
}

const NOUN_BY_ENTITY_TYPE: Record<RenderDataPayload['entityType'], string> = {
  inquiry: 'leads',
  event: 'events',
  contact: 'contacts',
  task: 'tasks',
}

/**
 * Renders a `render_data` payload as a grid of clickable entity cards, capped
 * at {@link MAX_RENDERED_CARDS}. Dock (~416px) and the full `/chat` page share
 * no mode signal, so the grid goes 1-up / 2-up via a container query instead
 * of a prop: `@container` on this wrapper, `@md:grid-cols-2` on the grid.
 */
export function EntityCardList({ payload }: { payload: RenderDataPayload }) {
  const { entities, totalRequested, truncated } = payload
  const totalCount = Math.max(totalRequested, entities.length)
  const showViewAll = entities.length > MAX_RENDERED_CARDS || truncated

  return (
    <div data-slot="entity-card-list" data-entity-type={payload.entityType} className="@container">
      <div className="grid gap-2 @md:grid-cols-2">
        {payload.entityType === 'inquiry' && payload.entities.slice(0, MAX_RENDERED_CARDS).map((inquiry) => (
          <InquiryEntityCard key={inquiry.id} inquiry={inquiry} />
        ))}
        {payload.entityType === 'event' && payload.entities.slice(0, MAX_RENDERED_CARDS).map((event) => (
          <EventEntityCard key={event.id} event={event} />
        ))}
        {payload.entityType === 'contact' && payload.entities.slice(0, MAX_RENDERED_CARDS).map((contact) => (
          <ContactEntityCard key={contact.id} contact={contact} />
        ))}
        {payload.entityType === 'task' && payload.entities.slice(0, MAX_RENDERED_CARDS).map((task) => (
          <TaskEntityCard key={task.id} task={task} />
        ))}
      </div>
      {showViewAll && (
        <Link
          to={LIST_ROUTE_BY_ENTITY_TYPE[payload.entityType]}
          className="mt-2 inline-block text-xs font-medium text-primary hover:underline"
        >
          View all {totalCount} {NOUN_BY_ENTITY_TYPE[payload.entityType]}
        </Link>
      )}
    </div>
  )
}
